import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useProCalculator } from '../hooks/useProCalculator';
import { useAppSettings } from '../hooks/useAppSettings';
import ProCalculatorFooter from './ProCalculatorFooter';
import SectionHelp from './SectionHelp';
import CookieAIColors from './CookieAIColors';
import ToggleSwitch from './ToggleSwitch';
import { Palette } from 'lucide-react';
import '../styles/ProCalculator.css';
import '../styles/ColorsSection.css';
import '../styles/ReferenceSection.css';

const ColorsSection = () => {
  const {
    colorNames,
    setColorNames,
    colorRows,
    // eslint-disable-next-line no-unused-vars -- 3.4.2026
    updateColorRow,
    updateColorPercentage,
    addColor,
    removeColor,
    cookieRows,
    useSliders,
    setUseSliders,
    getColorStyle,
    normalizeColorPercentages,
    isMobile,
    goToNextSection,
    activeSection,
    setActiveSection,
    getCanonicalColorName,
    loadedPlanName,
    showAdvanced,
    updateAdvancedFeatures,
    applyAIColors,
    user
  } = useProCalculator();

  const { settings: appSettings } = useAppSettings();
  const aiModel = appSettings?.color_mix_ai_model || 'Claude (Anthropic)';

  const [normalizeMsg, setNormalizeMsg] = useState(null);
  const [showAIColors, setShowAIColors] = useState(false);

  const handleNormalize = () => {
    const skipped = normalizeColorPercentages();
    if (skipped > 0) {
      setNormalizeMsg(`${skipped} row${skipped !== 1 ? 's' : ''} could not be adjusted — all colors for that cookie are set to 0%.`);
      setTimeout(() => setNormalizeMsg(null), 6000);
    } else {
      setNormalizeMsg(null);
    }
  };

  return (
    <div className="colors-section">
      <div className="section-header">
        <h2 className="section-title">Color Percentages</h2>
        {loadedPlanName && <span className="loaded-plan-name">Plan: {loadedPlanName}</span>}
      </div>
      {/* Help text container */}
      <div className="section-help-container">
        <SectionHelp sectionId="colors" isMobile={isMobile} />
      </div>

      {/* Unit preference box */}
      {user && (
        <div className="unit-prefs-box">
          <h3 className="unit-prefs-title">Calculation Detail</h3>
          <p className="unit-prefs-desc">Type your colors in yourself, or switch to Advanced to pull them from a photo instead.</p>
          <div className="unit-toggles">
            <div className="unit-toggle-group unit-toggle-group--advanced">
              <label className="reference-toggle-label">Simple</label>
              <ToggleSwitch isOn={showAdvanced} onToggle={(val) => updateAdvancedFeatures(val)} />
              <label className="reference-toggle-label advanced-label">
                <span className="advanced-label-full">Advanced</span>
                <span className="advanced-label-short">Adv</span>
              </label>
            </div>
          </div>
        </div>
      )}

      {!user && (
        <div className="unit-prefs-box">
          <p className="unit-prefs-desc unit-prefs-signin-hint">
            <a href="/login?tab=signup&redirect=/pro">Sign in free</a> to unlock Advanced mode and AI-powered tools.
          </p>
        </div>
      )}

      {/* ── AI Color Detection (Advanced only) ── */}
      {showAdvanced && (
        <div style={{ marginBottom: 8 }}>
          <button
            type="button"
            className="normalize-button"
            onClick={() => setShowAIColors(s => !s)}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <Palette size={18} />
            {showAIColors ? 'Hide AI Color Detection ▲' : 'Detect Colors from Photo ▼'}
          </button>
          {showAIColors && (
            <CookieAIColors
              aiModel={aiModel}
              existingColors={colorNames}
              onAcceptColors={(colors) => {
                applyAIColors(colors);
                setShowAIColors(false);
              }}
            />
          )}
        </div>
      )}

      <div className="color-management-row">
        <div className="color-names-container">
          <h3>Colors</h3>
          <div className="color-names-list">
            {colorNames.map((color, index) => (
              <div key={index} className="color-name-item">
                <div className="color-sample" style={getColorStyle(color)}></div>
                <input
                  type="text"
                  value={color}
                  onChange={(e) => {
                    const newColorNames = [...colorNames];
                    newColorNames[index] = e.target.value;
                    setColorNames(newColorNames);
                  }}
                  className="color-name-input"
                />
                {colorNames.length > 1 && (
                  <button
                    onClick={() => removeColor(index)}
                    className="btn-delete"
                    title="Remove Color"
                  >
                    <span className="minus-icon">-</span>
                    Remove
                  </button>
                )}
              </div>
            ))}

            <button
              onClick={addColor}
              className="btn-create"
              title="Add Color"
            >
              <span className="plus-icon">+</span> Add Color
            </button>
          </div>
        </div>

        {/* Cross-promo: help mixing an actual shade, not just planning percentages */}
        <div className="color-mix-nudge">
          <span className="color-mix-nudge-icon"><Palette size={22} /></span>
          <div className="color-mix-nudge-text">
            <strong>Color Mix AI</strong>{' '}
            will help you create your color shades.
          </div>
          <Link to="/color-mix" className="color-mix-nudge-btn">Try Color Mix AI</Link>
        </div>
      </div>

      <div className="input-type-toggle">
        <label className="slider-toggle-label">
          <input
            type="checkbox"
            checked={useSliders}
            onChange={(e) => setUseSliders(e.target.checked)}
            className="slider-toggle-input"
          />
          Use Percentages
          <span className="toggle-switch"></span>
          Use Sliders
        </label>
      </div>

      {/* Normalization Button - Visible only with 3+ colors */} 
      {colorNames.length >= 3 && (
        <div className="normalization-controls">
          <button 
            onClick={handleNormalize} 
            className="normalize-button"
            title="Adjust percentages in each row to sum to 100%"
          >
            Adjust All Rows to 100%
          </button>
          {normalizeMsg && (
            <div className="normalize-skip-msg">{normalizeMsg}</div>
          )}
        </div>
      )}
      
      <div className="color-percentages-table">
        <div className="color-percentages-header">
          <div className="cookie-name-header">Cookie</div>
          {colorNames.map((color, index) => (
            <div key={index} className="color-header">
              <div className="color-sample" style={getColorStyle(color)}></div>
              <span>{color}</span>
            </div>
          ))}
        </div>
        
        {colorRows.map((row, rowIndex) => {
          const numColors = colorNames.length;
          let isRowIncomplete = false;

          // Check row sum only if 3 or more colors exist
          if (numColors >= 3) {
            const percentages = row.percentages || [];
            const sum = percentages.reduce((acc, val) => acc + (Number(val) || 0), 0);
            // Use tolerance for floating point
            if (Math.abs(sum - 100) > 0.01) {
              isRowIncomplete = true;
            }
          }

          // Dynamically construct className for the row
          const rowClassName = `color-row ${isRowIncomplete ? 'incomplete-row' : ''}`;

          return (
            <div key={row.id || rowIndex} className={rowClassName}>
              <div className="cookie-name-field">
                {(() => { const n = cookieRows.find(c => c.id === row.cookieId)?.cookieName; return n && n !== 'Cookie Name' ? n : <em>Need Cookie Name</em>; })()}
              </div>
              
              {colorNames.map((color, colorIndex) => (
                <div key={colorIndex} className="color-percentage-field">
                  {useSliders ? (
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={row.percentages[colorIndex] ?? 0}
                      onChange={(e) => updateColorPercentage(row.cookieId, colorIndex, e.target.value)}
                      className="color-slider"
                      style={{ '--percentage': `${row.percentages[colorIndex] ?? 0}%` }} 
                    />
                  ) : (
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={row.percentages[colorIndex] ?? 0}
                      onChange={(e) => updateColorPercentage(row.cookieId, colorIndex, e.target.value)}
                      onFocus={(e) => e.target.select()}
                      className="color-input"
                      autoComplete="off"
                      name={`icing-pct-r${rowIndex}-c${colorIndex}`}
                      inputMode="numeric"
                    />
                  )}
                  <span className="percentage-label">{row.percentages[colorIndex] ?? 0}%</span>
                </div>
              ))}
            </div>
          );
        })}
      </div>
      
      {/* Footer with Save/Save As/Clear and Next buttons */}
      {activeSection !== 'all' && (
        <ProCalculatorFooter
          onBack={() => setActiveSection('dimensions')}
          backLabel="Back: Size & Shape"
          onNext={() => goToNextSection('colors')}
          nextLabel="Next: How Many"
        />
      )}
    </div>
  );
};

export default ColorsSection;
